import { EmployerDictionary, EmployerItem } from "../../models/employer.model";
import { BackendHttpService } from "../backendHttpService";

/**
 * Employer Dictionary Admin Service
 *
 * Administrative endpoint for employer dictionary management.
 *
 * This endpoint provides access to the employers dictionary which contains
 * a list of employer names used throughout the system.
 *
 * These endpoints are typically restricted to administrators.
 */
export class EmployerDictionaryAdminService extends BackendHttpService {
  // Cache for employers dictionary
  private employersDictionaryCache: EmployerItem[] | null = null;
  private rawEmployersDictionaryCache: EmployerDictionary | null = null;

  constructor() {
    super("WorkExperience"); // Base endpoint is 'WorkExperience'
  }

  // Clear cache when needed
  private clearCache(): void {
    this.employersDictionaryCache = null;
    this.rawEmployersDictionaryCache = null;
  }

  /**
   * Get raw employers dictionary as string array
   * GET /WorkExperience/employersDictionary
   */
  async getRawEmployersDictionary(): Promise<EmployerDictionary> {
    if (this.rawEmployersDictionaryCache) {
      return this.rawEmployersDictionaryCache;
    }

    try {
      this.rawEmployersDictionaryCache = await this.get<EmployerDictionary>(
        "/employersDictionary"
      );
      return this.rawEmployersDictionaryCache;
    } catch (error) {
      console.error("Error getting raw employers dictionary:", error);
      throw error;
    }
  }

  /**
   * Get employers dictionary processed with generated row numbers for UI display
   * GET /WorkExperience/employersDictionary (processed)
   */
  async getEmployersDictionary(): Promise<EmployerItem[]> {
    if (this.employersDictionaryCache) {
      return this.employersDictionaryCache;
    }

    try {
      const employersArray = await this.getRawEmployersDictionary();

      // Convert string array to array of objects with generated row numbers
      this.employersDictionaryCache = employersArray.map(
        (employerName, index) => ({
          rowNo: index + 1, // Generate sequential row numbers starting from 1
          name: employerName,
        })
      );

      return this.employersDictionaryCache;
    } catch (error) {
      console.error("Error processing employers dictionary:", error);
      throw error;
    }
  }

  /**
   * Method to manually refresh the cache if needed
   */
  async refreshEmployersDictionary(): Promise<EmployerItem[]> {
    this.clearCache();
    return this.getEmployersDictionary();
  }
}

// Create and export a singleton instance
export const employerDictionaryAdminService =
  new EmployerDictionaryAdminService();
